import React, { useState, useCallback, useMemo } from 'react';
import { Grid, TextField, IconButton } from '@material-ui/core';
import { Close as CloseIcon, Check as CheckIcon } from '@material-ui/icons';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { Body1, Circle, Header1, StatusBadge } from './blocks';

const metaText = css`
    font-size: 13px;
    line-height: 18px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
`;

// The design tokens StatusBadge's tones use (@onward-delivery/design tokens.css, loaded on :root), so a
// dot matches its carrier badge.
const READINESS_COLORS = {
    success: 'rgb(var(--color-primary-rgb))',
    warning: 'rgb(var(--color-warning-rgb))',
    danger: 'rgb(var(--color-destructive-rgb))',
};

// `tone`: a StatusBadge tone. Green shows a check, yellow and red a filled dot, anything else a grey ring.
const ReadinessItem = ({ tone, label }) => (
    <Body1
        data-tone={tone || 'none'}
        css={css`
            ${metaText};
            color: ${READINESS_COLORS[tone] || 'rgb(var(--color-muted-rgb))'};
        `}
    >
        {tone === 'success' ? (
            <CheckIcon
                css={css`
                    font-size: 14px;
                `}
            />
        ) : (
            <span
                css={css`
                    width: 7px;
                    height: 7px;
                    border-radius: 999px;
                    border: 1px solid currentColor;
                    background: ${READINESS_COLORS[tone] ? 'currentColor' : 'transparent'};
                `}
            />
        )}
        {label}
    </Body1>
);

// Page-specific `options`:
//   showCarrierStatus -- carrier pill (Auto Assign)
//   readiness         -- `(subregion) => ({ primary, tariff, integrated })` (Auto Assign): `primary` is a
//                        boolean, `tariff` / `integrated` are StatusBadge tones or null. The carrier
//                        pill doesn't use it: any carrier reads "Carrier", primary or not
//   readOnly          -- hides rename and delete (Rate Shop)
const SubregionListItem = ({ subregion, idx, isEditing, isSelected, callbacks, options }) => {
    const [name, setName] = useState(null);

    const updateSubregion = useCallback(() => {
        if (!name || name === subregion.name) return;

        callbacks
            .updateSubregion({
                subregion_id: subregion.subregion_id,
                name,
            })
            .then(() => {
                setName(null);
                callbacks.setEdit(subregion.subregion_id, false);
            });
    }, [name, subregion]);

    const [label, owner] = useMemo(() => {
        return [callbacks.genName(subregion, idx), callbacks.genOwner(subregion, idx)];
    }, [subregion, idx]);

    const canEdit = isSelected && !options?.readOnly;
    const zipCount = subregion.zips?.length || 0;
    const readiness = options?.readiness ? options.readiness(subregion) : null;
    const hasCarrier = !!subregion.carriers?.length;

    return (
        <Grid
            container
            direction="row"
            css={css`
                cursor: pointer;
                flex-wrap: nowrap;
                padding: 16px 20px;
                border-bottom: 1px solid ${colors.greys[4]};
                background: ${isSelected ? colors.greys[4] : colors.white.primary};
            `}
            onClick={() => {
                callbacks.resetEdit();
                callbacks.toggleSubregion(subregion.subregion_id);
            }}
            onMouseEnter={() => {
                callbacks.toggleSubregionHover(subregion.subregion_id);
            }}
            onMouseLeave={() => {
                callbacks.toggleSubregionHover(subregion.subregion_id);
            }}
        >
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    padding-top: 6px;
                    margin-right: 12px;
                `}
            >
                <Circle
                    css={css`
                        background: ${callbacks.genColor(subregion)};
                    `}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 1;
                    flex-basis: 0;
                    min-width: 0;
                    justify-content: center;
                    cursor: ${canEdit ? 'text' : 'pointer'};
                    margin-right: 12px;
                `}
                onClick={(e) => {
                    if (canEdit) {
                        if (name?.length > 0) {
                            updateSubregion();
                        } else {
                            callbacks.toggleEdit(subregion.subregion_id);
                        }

                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            >
                <>
                    {isEditing ? (
                        <TextField
                            autoFocus
                            fullWidth
                            variant="outlined"
                            value={name || subregion.name || ''}
                            onBlur={(e) => {
                                if (e.target.value.length > 0) {
                                    updateSubregion();
                                }
                            }}
                            onChange={(e) => {
                                setName(e.target.value);
                            }}
                            css={css`
                                input {
                                    font-size: 14px;
                                    padding: 7px;
                                }
                            `}
                        />
                    ) : (
                        <Grid
                            container
                            css={css`
                                flex-wrap: nowrap;
                                align-items: center;
                                justify-content: space-between;
                                gap: 8px;
                            `}
                        >
                            <Header1
                                css={css`
                                    font-size: 15px;
                                    font-weight: 700;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                    white-space: nowrap;
                                `}
                            >
                                {label}
                            </Header1>
                            {options?.showCarrierStatus ? (
                                <StatusBadge tone={hasCarrier ? 'success' : 'warning'} dot>
                                    {hasCarrier ? 'Carrier' : 'No carrier'}
                                </StatusBadge>
                            ) : null}
                        </Grid>
                    )}
                    {owner ? <Body1>{owner}</Body1> : null}
                    <Grid
                        container
                        css={css`
                            column-gap: 12px;
                            row-gap: 2px;
                            margin-top: 2px;
                        `}
                    >
                        <Body1
                            css={css`
                                ${metaText};
                                font-weight: 700;
                                color: ${zipCount ? colors.greens.primary : colors.greys.primary};
                            `}
                        >
                            {zipCount ? (
                                <CheckIcon
                                    css={css`
                                        font-size: 14px;
                                    `}
                                />
                            ) : null}
                            {`${zipCount} ${zipCount === 1 ? 'zip' : 'zips'}`}
                        </Body1>
                        {readiness ? (
                            <>
                                <ReadinessItem tone={readiness.primary ? 'success' : null} label="primary" />
                                <ReadinessItem tone={readiness.tariff} label="tariff" />
                                <ReadinessItem tone={readiness.integrated} label="integrated" />
                            </>
                        ) : null}
                    </Grid>
                </>
            </Grid>
            {!options?.readOnly ? (
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                        flex-basis: 0;
                        justify-content: center;
                    `}
                >
                    <IconButton
                        css={css`
                            padding: 8px;
                        `}
                        aria-label={`delete ${label}`}
                        // Not a click on the row, which would select the subregion.
                        onClick={(e) => {
                            e.stopPropagation();
                            callbacks.confirmDelete(subregion, label);
                        }}
                    >
                        <CloseIcon
                            css={css`
                                font-size: 14px;
                            `}
                        />
                    </IconButton>
                </Grid>
            ) : null}
        </Grid>
    );
};

export default SubregionListItem;
